import { useEffect, useState, useRef, useCallback } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { CheckCircle2, Clock, Package, Truck, Download, Share2, Home, User as UserIcon, RefreshCw } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useAuth } from '@/contexts/AuthContext';
import GraphicalReceipt from '@/components/GraphicalReceipt';
import { downloadReceipt, shareReceipt } from '@/lib/receiptImage';
import api, { formatPrice } from '@/lib/api';
import { toast } from 'sonner';

const STEPS = [
    { key: 'pending',    icon: Clock,        label: 'Order placed' },
    { key: 'processing', icon: Package,      label: 'Preparing' },
    { key: 'shipped',    icon: Truck,        label: 'On the way' },
    { key: 'delivered',  icon: CheckCircle2, label: 'Delivered' },
];

function ageLabel(ms) {
    if (ms < 2000) return 'just now';
    const s = Math.floor(ms / 1000);
    if (s < 60) return `${s}s ago`;
    const m = Math.floor(s / 60);
    return `${m}m ago`;
}

export default function OrderTrackPage() {
    const { reference } = useParams();
    const navigate = useNavigate();
    const { user } = useAuth();
    const [order, setOrder] = useState(null);
    const [notFound, setNotFound] = useState(false);
    const [busy, setBusy] = useState(false);
    const [lastCheckedAt, setLastCheckedAt] = useState(null);
    const [pullDelta, setPullDelta] = useState(0);
    const [refreshing, setRefreshing] = useState(false);
    const [, setNowTick] = useState(0); // re-render every 5s to update "last checked" label
    const receiptRef = useRef(null);
    const pollRef = useRef(null);
    const stopAtRef = useRef(Date.now() + 60_000);
    const pullStartY = useRef(null);
    const containerRef = useRef(null);

    const fetchOnce = useCallback(async () => {
        try {
            const { data } = await api.get(`/orders/track/${encodeURIComponent(reference)}`, { __nocache: true });
            setOrder(data);
            setLastCheckedAt(Date.now());
            return data;
        } catch (e) {
            if (e?.response?.status === 404) setNotFound(true);
            return null;
        }
    }, [reference]);

    useEffect(() => {
        if (!reference) return;
        fetchOnce();
        pollRef.current = setInterval(async () => {
            const d = await fetchOnce();
            const paid = d && (d.payment_status || '').toLowerCase() === 'paid';
            if (paid || Date.now() > stopAtRef.current) clearInterval(pollRef.current);
        }, 3000);
        return () => clearInterval(pollRef.current);
    }, [reference, fetchOnce]);

    // Tiny re-render tick so the "last checked" label stays fresh
    useEffect(() => {
        const t = setInterval(() => setNowTick((x) => x + 1), 5000);
        return () => clearInterval(t);
    }, []);

    const manualRefresh = useCallback(async () => {
        setRefreshing(true);
        // Restart the polling window so webhook delays after a manual check still benefit from live updates
        stopAtRef.current = Date.now() + 60_000;
        try { await fetchOnce(); }
        finally {
            setRefreshing(false);
            setPullDelta(0);
        }
    }, [fetchOnce]);

    // Pull-to-refresh (mobile only) — triggered when user drags down ≥ 70px from the very top
    useEffect(() => {
        const el = containerRef.current;
        if (!el) return;
        const onTouchStart = (e) => {
            if (window.scrollY > 0) return;
            pullStartY.current = e.touches[0].clientY;
        };
        const onTouchMove = (e) => {
            if (pullStartY.current == null) return;
            const delta = e.touches[0].clientY - pullStartY.current;
            if (delta > 0 && window.scrollY === 0) setPullDelta(Math.min(delta, 110));
        };
        const onTouchEnd = () => {
            if (pullDelta >= 70 && !refreshing) { manualRefresh(); }
            else setPullDelta(0);
            pullStartY.current = null;
        };
        el.addEventListener('touchstart', onTouchStart, { passive: true });
        el.addEventListener('touchmove', onTouchMove, { passive: true });
        el.addEventListener('touchend', onTouchEnd, { passive: true });
        return () => {
            el.removeEventListener('touchstart', onTouchStart);
            el.removeEventListener('touchmove', onTouchMove);
            el.removeEventListener('touchend', onTouchEnd);
        };
    }, [pullDelta, refreshing, manualRefresh]);

    if (notFound) {
        return (
            <div className="max-w-xl mx-auto px-4 py-16 text-center font-body">
                <h1 className="font-heading text-2xl font-bold text-gray-900 mb-2">Order not found</h1>
                <p className="text-sm text-gray-500 mb-6">We couldn't find an order for that reference. It may still be processing.</p>
                <Link to="/"><Button className="rounded-full bg-pink-600 hover:bg-pink-700">Back to Store</Button></Link>
            </div>
        );
    }
    if (!order) {
        return (
            <div className="max-w-3xl mx-auto px-4 py-10 animate-pulse space-y-4">
                <div className="h-8 w-64 bg-gray-100 rounded" />
                <div className="h-32 bg-gray-100 rounded-2xl" />
                <div className="h-48 bg-gray-100 rounded-2xl" />
            </div>
        );
    }

    const paid = (order.payment_status || '').toLowerCase() === 'paid';
    const activeStep = STEPS.findIndex((s) => s.key === (order.order_status || 'pending'));
    const checkedAgoMs = lastCheckedAt ? (Date.now() - lastCheckedAt) : 0;

    const handleDownload = async () => {
        if (!receiptRef.current) return;
        setBusy(true);
        try { await downloadReceipt(receiptRef.current, order.order_number); toast.success('Receipt downloaded'); }
        catch { toast.error('Could not generate receipt'); } finally { setBusy(false); }
    };
    const handleShare = async () => {
        if (!receiptRef.current) return;
        setBusy(true);
        try {
            const r = await shareReceipt(receiptRef.current, order.order_number, `My BeautyByChancy order #${order.order_number}`);
            if (r.shared) toast.success('Shared!');
            else if (r.downloaded) toast.success('Receipt downloaded');
        } catch { toast.error('Could not share receipt'); } finally { setBusy(false); }
    };

    return (
        <div ref={containerRef} className="max-w-3xl mx-auto px-4 py-8 fade-in-up relative" data-testid="order-track-page"
             style={{ transform: pullDelta > 0 ? `translateY(${pullDelta * 0.5}px)` : 'none', transition: pullDelta === 0 ? 'transform 240ms cubic-bezier(0.22,1,0.36,1)' : 'none' }}>

            {/* Pull-to-refresh visual indicator (mobile) */}
            {(pullDelta > 0 || refreshing) && (
                <div className="absolute top-0 left-1/2 -translate-x-1/2 -translate-y-14 flex flex-col items-center pointer-events-none"
                     style={{ opacity: refreshing ? 1 : Math.min(pullDelta / 70, 1) }} data-testid="pull-to-refresh-indicator">
                    <div className={`w-10 h-10 rounded-full bg-white border border-pink-100 shadow-sm flex items-center justify-center ${refreshing ? 'animate-spin' : ''}`}>
                        <RefreshCw className={`w-4 h-4 text-pink-600 ${!refreshing && pullDelta >= 70 ? 'rotate-180 transition-transform' : 'transition-transform'}`} />
                    </div>
                    <span className="text-[10px] font-body text-pink-600 mt-1">{refreshing ? 'Checking…' : pullDelta >= 70 ? 'Release to refresh' : 'Pull to refresh'}</span>
                </div>
            )}

            {/* Header row: Live-checked badge + manual refresh */}
            <div className="flex items-center justify-between mb-3 text-[11px] font-body text-gray-500">
                <span className="inline-flex items-center gap-1.5" data-testid="last-checked">
                    <span className={`w-1.5 h-1.5 rounded-full ${paid ? 'bg-green-500' : 'bg-yellow-500 animate-pulse'}`} />
                    {lastCheckedAt ? `Last checked ${ageLabel(checkedAgoMs)}` : 'Checking…'}
                </span>
                <button onClick={manualRefresh} disabled={refreshing}
                        className="inline-flex items-center gap-1 text-pink-600 hover:text-pink-700 disabled:opacity-50 tap-target" data-testid="tap-refresh">
                    <RefreshCw className={`w-3.5 h-3.5 ${refreshing ? 'animate-spin' : ''}`} /> Tap to refresh
                </button>
            </div>

            {/* Status banner */}
            <div className={`rounded-2xl px-5 py-5 mb-6 border ${paid ? 'bg-green-50 border-green-100' : 'bg-yellow-50 border-yellow-100'}`}
                 data-testid="track-status-banner">
                <div className="flex items-center gap-3 mb-1">
                    <div className={`w-9 h-9 rounded-full flex items-center justify-center ${paid ? 'bg-green-500 text-white' : 'bg-yellow-500 text-white animate-pulse'}`}>
                        {paid ? <CheckCircle2 className="w-5 h-5" /> : <Clock className="w-5 h-5" />}
                    </div>
                    <div>
                        <h1 className="font-heading text-xl font-bold text-gray-900" data-testid="track-status-title">
                            {paid ? 'Payment Confirmed — Processing' : 'Waiting for payment confirmation…'}
                        </h1>
                        <p className="text-xs font-body text-gray-500 mt-0.5">
                            Order <strong>{order.order_number}</strong> · <strong className="text-pink-600">{formatPrice(order.total)}</strong>
                        </p>
                    </div>
                </div>
                {!paid && (
                    <p className="text-xs font-body text-yellow-800 mt-2">
                        Hang tight — we're confirming your payment with Paystack. This page will update automatically, or pull down (on mobile) / tap refresh to check now.
                    </p>
                )}
            </div>

            {/* Timeline */}
            <div className="bg-white rounded-2xl border border-gray-100 p-5 mb-6" data-testid="track-timeline">
                <h2 className="text-[11px] uppercase tracking-wider font-body text-gray-400 mb-3">Order Timeline</h2>
                <div className="grid grid-cols-4 gap-2">
                    {STEPS.map((s, i) => {
                        const reached = paid && i <= Math.max(activeStep, 0);
                        const Icon = s.icon;
                        return (
                            <div key={s.key} className="flex flex-col items-center text-center" data-testid={`track-step-${s.key}`}>
                                <div className={`w-9 h-9 rounded-full flex items-center justify-center mb-1.5 transition-colors duration-300 ${reached ? 'bg-pink-600 text-white' : 'bg-gray-100 text-gray-400'}`}>
                                    <Icon className="w-4 h-4" />
                                </div>
                                <div className={`text-[11px] font-body ${reached ? 'text-gray-900 font-medium' : 'text-gray-400'}`}>{s.label}</div>
                            </div>
                        );
                    })}
                </div>
            </div>

            {/* Receipt preview */}
            <div className="rounded-2xl border border-gray-100 bg-white shadow-sm p-3 sm:p-4 mb-6 overflow-x-auto">
                <div style={{ width: 720, margin: '0 auto' }}>
                    <GraphicalReceipt ref={receiptRef} order={order} />
                </div>
            </div>

            {/* Actions */}
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-3">
                <Button onClick={handleDownload} disabled={busy} variant="outline"
                        className="rounded-full h-12 gap-2 border-pink-200 text-pink-700 hover:bg-pink-50 tap-target" data-testid="track-download">
                    <Download className="w-4 h-4" /> Download HD Receipt
                </Button>
                <Button onClick={handleShare} disabled={busy}
                        className="rounded-full h-12 gap-2 bg-pink-600 hover:bg-pink-700 text-white tap-target" data-testid="track-share">
                    <Share2 className="w-4 h-4" /> Share Receipt
                </Button>
                <Button onClick={() => navigate(user ? '/account' : '/')} variant="outline"
                        className="rounded-full h-12 gap-2 tap-target" data-testid="track-continue">
                    {user ? <UserIcon className="w-4 h-4" /> : <Home className="w-4 h-4" />} {user ? 'View all my orders' : 'Back to Store'}
                </Button>
            </div>
        </div>
    );
}
